import Button from 'components/Button';
import { IntrinsicSizeImg } from 'components/Img';
import { AnchorLink } from 'components/Link';
import Message from 'components/Message';
import PaymentMethodLogos from 'components/PaymentMethodLogos';
import Price from 'components/Price';
import { Prices } from 'components/Product';
import RichText from 'components/RichText';
import Text from 'components/Text';
import { PRODUCT_DETAILS_TECHNICAL_ATTRIBUTES_ID } from 'constants/ids';
import { useSafeFieldValues } from 'hooks';
import type { Product, VariantId } from 'models/product';
import { getEnergySymbol } from 'utils/business-logic';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { getWeightedPercentage } from 'utils/math';

import ButtonPanel from './ButtonPanel';
import { SizePicker, StylePicker, VariantPicker } from './Pickers';
import ProductDetailsAccessoriesUpsell from './ProductDetailsAccessoriesUpsell';
import ProductDetailsCampaigns, {
	type AddCampaignData,
} from './ProductDetailsCampaign';
import { useReviewContext } from './ProductDetailsContext';
import {
	MIN_ANSWER_COUNT,
	TOO_LARGE_PERCENTAGE,
	TOO_SMALL_PERCENTAGE,
} from './ProductFit';
import ProductPrint from './ProductPrint';
import { StockBalanceList } from './StockInformation';

interface Props {
	onCampaignPackageAddToCartClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	onCampaignPackageAddToWishlistClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	onCreditSimulationClick: () => void;
	onEnergyLabelClick: () => void;
	onMainPurchaseButtonClick: () => void;
	onSelectedAccessoryUpsellAdd: (id: VariantId) => void;
	onSelectedAccessoryUpsellRemove: (id: VariantId) => void;
	onSizeGuideClick: () => void;
	onWishlistButtonClick: () => void;
	product: Product;
	selectedAccessoryUpsellIds: VariantId[];
}

export default function ProductDetailsSidebar({
	onCampaignPackageAddToCartClick,
	onCampaignPackageAddToWishlistClick,
	onCreditSimulationClick,
	onEnergyLabelClick,
	onMainPurchaseButtonClick,
	onSelectedAccessoryUpsellAdd,
	onSelectedAccessoryUpsellRemove,
	onSizeGuideClick,
	onWishlistButtonClick,
	product,
	selectedAccessoryUpsellIds,
}: Props) {
	const { t } = useI18n();
	const { fit3Summary, isLoadingFit } = useReviewContext();

	const {
		content,
		customization,
		isCustomizable,
		productSheetLink,
		recommendedAdditionsCount,
		showCreditSimulationLink,
		sizeGuideUrl,
		staffPrice,
		stylesAndColors,
		variants,
	} = product;

	const safeContent = useSafeFieldValues(product.content);

	const energySymbol = getEnergySymbol(product);

	const perceivedFitPercentage =
		fit3Summary?.options?.length && fit3Summary.count >= MIN_ANSWER_COUNT
			? getWeightedPercentage(...fit3Summary.options.map((opt) => opt.count))
			: undefined;
	const perceivedFitText =
		// Explicitly check undefined to not exclude zero.
		perceivedFitPercentage === undefined
			? undefined
			: perceivedFitPercentage < TOO_SMALL_PERCENTAGE
				? safeContent.productPerceivedFitSmallText
				: perceivedFitPercentage > TOO_LARGE_PERCENTAGE
					? safeContent.productPerceivedFitLargeText
					: safeContent.productPerceivedFitPerfectText;

	return (
		<>
			<Prices
				displayPrice={product.displayPrice}
				basePrice={product.basePrice}
			/>

			{Boolean(energySymbol?.src || productSheetLink) && (
				<div className="my-8 flex items-center">
					{energySymbol?.src && (
						<AnchorLink
							anchor={PRODUCT_DETAILS_TECHNICAL_ATTRIBUTES_ID}
							onClick={onEnergyLabelClick}
							className="mr-2 hover:opacity-80"
						>
							<IntrinsicSizeImg
								loading="eager"
								asset={energySymbol}
								alt={t('product_details_technical_specification_heading')}
								className="h-12 w-auto"
							/>
						</AnchorLink>
					)}
					{productSheetLink && (
						<Text as="pSmall">
							<a
								href={productSheetLink}
								target="_blank"
								rel="nofollow noopener noreferrer"
								className="underline hover:underline-strong"
							>
								{product.productSheetLinkDescription ||
									t('product_details_product_sheet_button')}
							</a>
						</Text>
					)}
				</div>
			)}

			{staffPrice && (
				<Message
					heading={safeContent.staffPriceHeading}
					variant="information"
					className="mt-r-lg"
				>
					<p>{safeContent.staffPriceText}</p>
					<Price
						className="mt-4"
						price={staffPrice}
						size="xs"
						forceRegularPrice
					/>
				</Message>
			)}

			<ProductDetailsCampaigns
				className="mt-4"
				heading={safeContent.campaignsHeading}
				campaigns={product.campaigns}
				campaignValidToText={safeContent.campaignValidToText}
				packagePriceLabel={safeContent.volumePriceLabel}
				onCampaignPackageAddToCartClick={onCampaignPackageAddToCartClick}
				onCampaignPackageAddToWishlistClick={
					onCampaignPackageAddToWishlistClick
				}
			/>

			{showCreditSimulationLink && (
				<Button
					variant="text"
					className="mt-6 text-left"
					onClick={onCreditSimulationClick}
					aria-haspopup="dialog"
				>
					{safeContent.openCreditSimulationPopoverButtonText}
				</Button>
			)}

			<StylePicker
				className="mt-8"
				buttonText={safeContent.openStylePickerPopoverButtonText}
				variantTitle={safeContent.stylePickerPopoverTitle}
				product={product}
				variants={stylesAndColors}
			/>
			{!sizeGuideUrl && (
				<VariantPicker
					className={stylesAndColors?.length ? 'mt-4' : 'mt-8'}
					fallbackButtonText={safeContent.openStylePickerPopoverButtonText}
					fallbackPopoverTitle={safeContent.stylePickerPopoverTitle}
					product={product}
					variants={variants}
				/>
			)}
			{sizeGuideUrl && is.arrayWithLength(variants) && (
				<SizePicker
					className={stylesAndColors?.length ? 'mt-4' : 'mt-8'}
					fit3Summary={fit3Summary}
					fallbackButtonText={safeContent.openStylePickerPopoverButtonText}
					fallbackPopoverTitle={safeContent.stylePickerPopoverTitle}
					product={product}
					variants={variants}
					fitText={
						!isLoadingFit && perceivedFitText
							? `${safeContent.productPerceivedFitText}: ${perceivedFitText}`
							: undefined
					}
					isLoadingFit={variants.length > 1 && isLoadingFit}
					sizeGuidePopoverButton={
						<Button
							variant="text"
							size="sm"
							onClick={onSizeGuideClick}
							aria-haspopup="dialog"
							className="min-h-0 py-0"
						>
							{safeContent.openSizeGuidePopoverButtonText}
						</Button>
					}
				/>
			)}

			{isCustomizable && customization && (
				<ProductPrint
					className="mt-3"
					productId={product.id}
					printOnClothes={customization}
				/>
			)}

			{recommendedAdditionsCount > 0 && (
				<ProductDetailsAccessoriesUpsell
					className="mt-8"
					title={safeContent.accessoriesUpsellHeading}
					productId={product.id}
					accessoryCount={recommendedAdditionsCount}
					onSelectedAdd={onSelectedAccessoryUpsellAdd}
					onSelectedRemove={onSelectedAccessoryUpsellRemove}
					selectedIds={selectedAccessoryUpsellIds}
				/>
			)}

			<ButtonPanel
				selectedAccessoryUpsellIds={selectedAccessoryUpsellIds}
				onPurchaseButtonClick={onMainPurchaseButtonClick}
				onWishlistButtonClick={onWishlistButtonClick}
			/>
			{content?.trustUsps?.value && (
				<RichText
					html={content.trustUsps.value}
					className="mt-4"
					checkMarkListType="plain"
				/>
			)}
			<PaymentMethodLogos
				className={content?.trustUsps?.value ? 'mt-3' : 'mt-4'}
				images={content?.paymentIcons?.value.assets.flat()}
			/>
			<StockBalanceList className="mt-r-lg" product={product} />
		</>
	);
}
ProductDetailsSidebar.displayName = 'ProductDetailsSidebar';
